import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { X, CheckCircle2, XCircle, Image as ImageIcon } from 'lucide-react';

interface RequirementItem {
    id: number;
    text: string;
    goodImg: string;
    badImg: string;
}

interface ImageRequirementsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onContinue: () => void;
}

const requirements: RequirementItem[] = [
    {
        id: 1,
        text: "1 person in the photo",
        goodImg: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=crop",
        badImg: "https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=200&h=200&fit=crop",
    },
    {
        id: 2,
        text: "Face is clear and close to the camera",
        goodImg: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop",
        badImg: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop&blur=50",
    },
    {
        id: 3,
        text: "Face forward, no profile shots",
        goodImg: "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=200&h=200&fit=crop",
        badImg: "https://images.unsplash.com/photo-1542909168-82c3e7fdca5c?w=200&h=200&fit=crop&rotate=90",
    }
];

export const ImageRequirementsModal: React.FC<ImageRequirementsModalProps> = ({ isOpen, onClose, onContinue }) => {
    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('blocking-modal-open');
        } else {
            document.body.classList.remove('blocking-modal-open');
        }

        return () => {
            document.body.classList.remove('blocking-modal-open');
        };
    }, [isOpen]);

    if (typeof document === 'undefined') {
        return null;
    }

    return createPortal((
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-70 flex items-center justify-center p-0 sm:p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
                    />

                    {/* Modal Content */}
                    <motion.div
                        initial={{ y: "100%", opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: "100%", opacity: 0 }}
                        transition={{ type: "spring", damping: 30, stiffness: 300 }}
                        className="relative w-full max-w-md bg-[#F8F9FE] dark:bg-gray-900 h-full sm:h-auto sm:rounded-[40px] overflow-hidden shadow-2xl flex flex-col"
                    >
                        {/* Header */}
                        <div className="p-6 flex items-center justify-between">
                            <button onClick={onClose} className="p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-full transition-colors">
                                <X size={24} className="text-gray-900 dark:text-white" />
                            </button>
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center">
                                    <ImageIcon size={18} className="text-white" />
                                </div>
                                <span className="font-bold text-gray-900 dark:text-white">AR Drawing</span>
                            </div>

                        </div>

                        <div className="px-8 pt-4 pb-8 text-center">
                            <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-2 tracking-tight">
                                Upload a face photo
                            </h2>
                            <p className="text-gray-400 dark:text-gray-500 font-medium">
                                Some photo requirements
                            </p>
                        </div>

                        {/* Requirements List */}
                        <div className="flex-1 overflow-y-auto px-4 space-y-4 pb-24 no-scrollbar">
                            {requirements.map((req) => (
                                <div key={req.id} className="bg-white dark:bg-gray-800 rounded-[32px] p-6 flex items-center justify-between shadow-xs border border-gray-50 dark:border-gray-700">
                                    <p className="text-gray-800 dark:text-gray-200 font-bold text-sm max-w-[120px] leading-tight">
                                        {req.text}
                                    </p>
                                    <div className="flex gap-3">
                                        {/* Good Example */}
                                        <div className="relative">
                                            <div className="w-16 h-16 rounded-2xl overflow-hidden">
                                                <img src={req.goodImg} alt="Good" className="w-full h-full object-cover" />
                                            </div>
                                            <div className="absolute -bottom-1 -right-1 bg-white dark:bg-gray-900 rounded-full shadow-md">
                                                <CheckCircle2 className="text-green-500" size={18} fill="currentColor" />
                                            </div>
                                        </div>
                                        {/* Bad Example */}
                                        <div className="relative">
                                            <div className="w-16 h-16 rounded-2xl overflow-hidden">
                                                <img src={req.badImg} alt="Bad" className="w-full h-full object-cover" />
                                            </div>
                                            <div className="absolute -bottom-1 -right-1 bg-white dark:bg-gray-900 rounded-full shadow-md">
                                                <XCircle className="text-red-500" size={18} fill="currentColor" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Action Button */}
                        <div className="absolute bottom-0 inset-x-0 p-6 bg-linear-to-t from-[#F8F9FE] via-[#F8F9FE] to-transparent dark:from-gray-900 dark:via-gray-900">
                            <button
                                onClick={onContinue}
                                className="w-full py-5 bg-[#FF2D55] text-white rounded-[24px] font-black text-lg shadow-xl shadow-pink-500/30 active:scale-[0.98] transition-all"
                            >
                                Upload a photo
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    ), document.body);
};
